'use client'

import { type ReactNode } from 'react'

import { signIn } from 'next-auth/react'
import { FormProvider, useForm, type SubmitHandler } from 'react-hook-form'
import Swal from 'sweetalert2'
import { z } from 'zod'

import { zodResolver } from '@hookform/resolvers/zod'

import { env } from '@/env'
import { useAuthContext } from '@/features/auth/contexts/AuthContext'
import { cn } from '@/shared/lib/utils'
import { api } from '@/shared/services/api'

import { validationSchema } from './Fields/validationSchema'

type SignInFormData = z.infer<typeof validationSchema>

interface SignInFormProps {
  onSignIn?: (result: {
    ok: boolean
    status?: number
    url?: string
    error?: string
    callbackUrl: string
  }) => void
  onError?: (result: {
    ok: boolean
    status?: number
    url?: string | null
    error?: string
  }) => void
  className?: string
  children?: ReactNode
}

export function SignInForm({
  onSignIn,
  onError,
  className = '',
  children
}: SignInFormProps) {
  const {
    callbackUrl,
    setCurrentEmail,
    goToResendConfirmation,
    currentEmail,
    isLoading,
    setIsLoading
  } = useAuthContext()

  const methods = useForm<SignInFormData>({
    defaultValues:
      env.NEXT_PUBLIC_PGEE_APP_ENV === 'development'
        ? { login: 'admin@mail.com', password: '123123123' }
        : { login: currentEmail ?? '', password: '' },
    resolver: zodResolver(validationSchema)
  })

  const handleSignIn: SubmitHandler<SignInFormData> = async data => {
    if (isLoading) return
    setIsLoading(true)

    const { error, ok = false, status, url } = await signIn('domain-sign-in', {
      login: data.login,
      password: data.password,
      callbackUrl,
      redirect: false
    })

    if (error) {
      setCurrentEmail(data.login)
      onError?.({ error, ok, status, url })

      if (!onError) {
        switch (error) {
          case 'invalid_password':
            methods.setError('password', {
              type: 'manual',
              message: 'Senha incorreta'
            })
            break
          case 'login_not_found':
            methods.setError('login', {
              type: 'manual',
              message: 'E-mail não encontrado'
            })
            break
          case 'unconfirmed':
            methods.setError('password', {
              type: 'manual',
              message: 'Conta não confirmada'
            })
            await Swal.fire({
              icon: 'error',
              title: 'Conta não confirmada',
              text: 'Sua conta foi registrada mas ainda não confirmada. Clique abaixo para reenviar o código.'
            })
            goToResendConfirmation()
            break
          case 'account_pending_deletion': {
            // Conta em carência de exclusão (LGPD): oferece o cancelamento.
            const result = await Swal.fire({
              icon: 'warning',
              title: 'Conta com exclusão pendente',
              text: 'Você solicitou a exclusão desta conta. Deseja cancelar a exclusão e entrar novamente?',
              showCancelButton: true,
              confirmButtonText: 'Cancelar exclusão e entrar',
              cancelButtonText: 'Voltar'
            })
            if (result.isConfirmed) {
              try {
                await api.post('/public/account_deletions/cancel', {
                  login: data.login,
                  password: data.password
                })
                const retry = await signIn('domain-sign-in', {
                  login: data.login,
                  password: data.password,
                  callbackUrl,
                  redirect: false
                })
                if (retry?.ok) {
                  window.location.replace(callbackUrl)
                  return
                }
              } catch {
                await Swal.fire({
                  icon: 'error',
                  title: 'Não foi possível cancelar',
                  text: 'Tente novamente mais tarde.'
                })
              }
            }
            break
          }
          default:
            methods.setError('password', {
              type: 'manual',
              message: 'Credenciais inválidas'
            })
        }
      }
    } else if (ok) {
      onSignIn?.({
        error,
        ok,
        status,
        url: url || undefined,
        callbackUrl
      })
      if (!onSignIn) window.location.replace(callbackUrl)
      return
    }

    setIsLoading(false)
  }

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(handleSignIn)}
        className={cn('grid gap-6', className)}
      >
        {children}
      </form>
    </FormProvider>
  )
}
